import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { callGet } from '@/api/fetcher';
import { useIntegrations } from '@/hooks/useIntegrations';
import { ConversationSource, Attachment as AttachmentType, AuthorType } from '@/shared/types/chat';
import { IntegrationType } from '@/shared/types/integration';

export const buildMinioUrl = (source: ConversationSource, attachment: AttachmentType) => {
  if (!source?.deskId || !source?.sessionId || !attachment?.filename) {
    return null;
  }
  const filename = encodeURIComponent(attachment.filename);
  const title = encodeURIComponent(attachment.title || attachment.filename);
  return `/www/attachment-url/${source.deskId}/${source.sessionId}/${attachment.id}/${filename}/${title}`;
};

const STORED_FILE_PATH = /^\/conversations\/[^/]+\/([^/]+)\/([^/]+)\/([^/]+)$/;

/** Routes a bot file stored in this session through the attachment route, which signs a fresh URL. */
export const buildStoredFileUrl = (
  source: ConversationSource | undefined,
  url: string | undefined,
  name?: string
): string | undefined => {
  if (!url || !source?.deskId || !source.sessionId) {
    return url;
  }
  try {
    const { pathname, searchParams } = new URL(url);
    const match = pathname.match(STORED_FILE_PATH);
    if (!match || match[1] !== source.deskId || match[2] !== source.sessionId) {
      return url;
    }
    const fileName = decodeURIComponent(match[3]);
    const disposition = searchParams.get('response-content-disposition');
    const title = disposition?.match(/filename="([^"]+)"/)?.[1] ?? name ?? fileName;
    return buildMinioUrl(source, { id: fileName, filename: fileName, title } as AttachmentType)!;
  } catch {
    return url;
  }
};

/** @deprecated Remove once old WhatsApp chats without MinIO replicas are gone. */
export const buildWhatsAppMediaUrl = (source: ConversationSource, attachment: AttachmentType) => {
  if (!source?.deskId || !source?.integrationId || !attachment?.id) {
    return null;
  }
  return `/www/api/v1/desks/${source.deskId}/integrations/${source.integrationId}/whatsapp/media/${attachment.id}`;
};

export const getAttachmentUrl = (
  source: ConversationSource,
  attachment: AttachmentType,
  authorType: `${AuthorType}`
): { minioUrl: string | null; whatsAppUrl: string | null } => {
  const isWhatsApp = source?.channel === IntegrationType.WHATSAPP;
  const hasFilename = Boolean(attachment?.filename);

  // Attachments with a filename have been stored in MinIO
  if (
    hasFilename &&
    source?.sessionId &&
    (authorType === 'agent' ||
      source.channel === IntegrationType.WEB ||
      source.channel === IntegrationType.EMAIL ||
      isWhatsApp)
  ) {
    return { minioUrl: buildMinioUrl(source, attachment), whatsAppUrl: null };
  }

  // @deprecated — old WhatsApp chats without MinIO replicas. Remove once legacy attachments expire.
  if (isWhatsApp) {
    return { minioUrl: null, whatsAppUrl: buildWhatsAppMediaUrl(source, attachment) };
  }

  return { minioUrl: null, whatsAppUrl: null };
};

export const createMediaUrl = (file, mimeType: string) => {
  if (file) {
    const uint8Array = new Uint8Array(file.data ?? file);
    const blob = new Blob([uint8Array], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const cleanup = () => URL.revokeObjectURL(url);

    return { url, cleanup };
  }

  return { url: null, cleanup: () => {} };
};

export const useAttachment = (
  source: ConversationSource,
  attachment: AttachmentType,
  authorType: `${AuthorType}`,
  showMedia: boolean = true
) => {
  const { t } = useTranslation();
  const [mediaUrl, setMediaUrl] = useState<string | null>('');

  const isWhatsApp = source?.channel === IntegrationType.WHATSAPP;
  const { minioUrl, whatsAppUrl } = getAttachmentUrl(source, attachment, authorType);
  const routerUrl = minioUrl || whatsAppUrl;
  // Only the deprecated fallback reads the integration, which a chat role may not be allowed to.
  const needsIntegration = isWhatsApp && !minioUrl;

  const { integration, getError } = useIntegrations(
    needsIntegration ? source?.deskId : undefined,
    source?.integrationId
  );

  const {
    data: file = null,
    isLoading,
    status,
  } = useQuery<Buffer | string>({
    queryKey: isWhatsApp ? ['whatsapp-attachment', attachment.id] : [minioUrl],
    queryFn: async () => {
      if (minioUrl) {
        try {
          const res = await callGet(minioUrl, { responseType: 'arrayBuffer' });
          return await res.arrayBuffer();
        } catch (err) {
          if (!isWhatsApp) {
            throw err;
          }
          // MinIO failed for WhatsApp, fall through to deprecated fallback below
        }
      }
      // @deprecated — WhatsApp media API fallback for old chats without MinIO replicas.
      // Remove once legacy attachments expire from Meta's API; only MinIO will remain.
      return await callGet(buildWhatsAppMediaUrl(source, attachment));
    },
    enabled: showMedia && Boolean(routerUrl) && !(needsIntegration && !integration),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  let errorState: Error;
  if (needsIntegration && getError) {
    errorState = new Error(
      t('conversation.attachment_error_no_longer_available', { 0: attachment.type })
    );
  } else if (routerUrl && showMedia && !isLoading && status === 'error') {
    errorState = new Error(t('conversation.attachment_error_no_valid_url', { 0: attachment.type }));
  }

  useEffect(() => {
    if (file) {
      const { url, cleanup } = createMediaUrl(file, attachment.mime_type);
      if (url) {
        setMediaUrl(url);
      }
      return cleanup;
    }
  }, [attachment?.mime_type, file]);

  useEffect(() => {
    if (attachment?.url) {
      setMediaUrl(attachment.url);
    }
  }, [attachment?.url]);

  return { file, isLoading, error: errorState as Error | null, mediaUrl };
};
